import {
  ADMIN_FILE_IMPORT_RESULT_LOADED,
  ADMIN_FILE_IMPORT_RESULT_CLEAR,
  ADMIN_FILE_IMPORTS_LOADED,
  ADMIN_FILE_IMPORTS_CLEAR,
  ADMIN_AUTH_API_ERROR,
} from '../actionTypes';
import * as api from '../apiHelper';
import {
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
  setFetchLoaderLoading,
  setFetchLoaderReset,
} from '../fetchLoaderActions';
import {
  arriveNotice,
  arriveCustomNotice,
} from '../noticeActions';

// --- Reducer corresponding actions ---
export function fileImportResultLoaded(result) {
  return {
    type: ADMIN_FILE_IMPORT_RESULT_LOADED,
    payload: { result },
  };
}

export function fileImportResultClear() {
  return { type: ADMIN_FILE_IMPORT_RESULT_CLEAR };
}

export function fileImportsLoaded(imports) {
  return {
    type: ADMIN_FILE_IMPORTS_LOADED,
    payload: { imports },
  };
}

export function fileImportsClear() {
  return { type: ADMIN_FILE_IMPORTS_CLEAR };
}

// --- CSV row count helpers ---
// Use environment variable if provided, defaulting to 5001 (1 header + up to 5000 data rows)
const MAX_CSV_TOTAL_ROWS = Number(process.env.MAX_CSV_TOTAL_ROWS || '5001');

function countCsvLines(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target.result || '';
      const lines = text.split(/\r\n|\n|\r/);

      // Ignore a trailing empty line that some editors add
      const filteredLines = lines.filter(
        (line, index) => !(index === lines.length - 1 && line.trim() === ''),
      );

      resolve(filteredLines.length);
    };

    reader.onerror = () => {
      reject(reader.error || new Error('Unable to read CSV file'));
    };

    reader.readAsText(file);
  });
}

async function enforceCsvRowLimit(body, dispatch) {
  const file = body && body.file;

  if (file instanceof File) {
    const totalLines = await countCsvLines(file);

    if (totalLines > MAX_CSV_TOTAL_ROWS) {
      const message = 'CSV contains more than 5k rows. Please split the CSV into multiple files and try again.';
      dispatch(arriveNotice(message));
      throw new Error(message);
    }
  }
}

// --- Helper actions ---
// Shared file import submission handler
async function submitFileImportApi(path, body, token, dispatch, method = 'POST') {
  // Enforce CSV row limit for all file imports
  await enforceCsvRowLimit(body, dispatch);

  const response = await api.mixedFormDataRequest({
    path,
    version: 'gateway',
    method,
    token,
    body,
    isAdminRoute: true,
    customError: true,
  });
  const json = await response.json();
  const camelJson = api.camelizeKeys(json);

  // Handle partial success (some rows processed despite errors)
  // If fileImportId is present, some rows were processed and record was added to DB
  if (camelJson.fileImportId) {
    dispatch(fileImportResultLoaded(camelJson));
    return camelJson;
  }
  // Complete failure or error response (no rows processed)
  if (!response.ok) {
    // Handle potential unauthorized or token expiration
    if (response.status === 401 || response.status === 403) {
      const message = api.formatJsonError(camelJson.error || camelJson.message);
      dispatch({
        type: ADMIN_AUTH_API_ERROR,
        error: { status: response.status, message },
      });
    }

    if (camelJson?.errors && camelJson.errors.length > 0) {
      const customError = api.extractErrorMessage(camelJson.errors);
      dispatch(arriveCustomNotice(customError));
      throw customError;
    } else {
      const formattedMessage = api.formatJsonError(camelJson.error || camelJson.message);
      const error = new Error(formattedMessage);
      dispatch(arriveNotice(formattedMessage));
      throw error;
    }
  }
  return camelJson;
}

// --- API functions ---
async function submitSetReviewResultsApi(body, token, dispatch) {
  return submitFileImportApi('results/reviews', body, token, dispatch, 'PUT');
}

async function submitDmcaTakedownNoticesApi(body, token, dispatch) {
  return submitFileImportApi('compliance/notices/takedown', body, token, dispatch);
}

async function submitDmcaRetractionNoticesApi(body, token, dispatch) {
  return submitFileImportApi('compliance/notices/retraction', body, token, dispatch);
}

async function submitDmcaEscalationApi(body, token, dispatch) {
  return submitFileImportApi('compliance/notices/escalation', body, token, dispatch);
}

async function submitResultsImportsApi(body, token, dispatch) {
  return submitFileImportApi('results/imports', body, token, dispatch);
}

async function submitCreateBulkSearchesApi(body, token, dispatch) {
  return submitFileImportApi('admin/searches/bulk', body, token, dispatch);
}

async function submitMarkUrlsDownApi(body, token, dispatch) {
  return submitFileImportApi('compliance/urls/status', body, token, dispatch);
}

async function submitTakedownStatsApi(body, token, dispatch) {
  return submitFileImportApi('compliance/urls/takedown-stats', body, token, dispatch);
}

async function submitMarkHostsDownApi(body, token, dispatch) {
  return submitFileImportApi('compliance/hosts/status', body, token, dispatch);
}

async function submitDowncheckApi(body, token, dispatch) {
  return submitFileImportApi('compliance/urls/downcheck', body, token, dispatch);
}

async function fetchFileImportsApi(token, args, dispatch) {
  const response = await api.nestedParamsGet({
    path: 'common/imports',
    version: 'gateway',
    params: args,
    token,
    dispatch,
    isAdminRoute: true,
  });
  const json = await response.json();
  return api.camelizeKeys(json);
}

// --- Public components actions ---
export function clearFileImportResultRequest() {
  return (dispatch) => {
    dispatch(fileImportResultClear());
  };
}

export function submitSetReviewStatusImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      await submitSetReviewResultsApi(formValues, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitDmcaTakedownNoticesImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      await submitDmcaTakedownNoticesApi(formValues, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitDmcaEscalationImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      await submitDmcaEscalationApi(formValues, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitCloudflareAdhocImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      /* eslint-disable-next-line camelcase */
      const body = { ...formValues, is_adhoc: true };
      await submitDmcaTakedownNoticesApi(body, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitResultsImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      await submitResultsImportsApi(formValues, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitCreateBulkSearchesImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      await submitCreateBulkSearchesApi(formValues, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitMarkUrlsDownImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      await submitMarkUrlsDownApi(formValues, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitTakedownStatsImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      await submitTakedownStatsApi(formValues, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitMarkHostsDownImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      await submitMarkHostsDownApi(formValues, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitDowncheckUrlsImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      await submitDowncheckApi(formValues, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitDmcaRetractionNoticesImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setSubmissionLoaderLoading());
      await submitDmcaRetractionNoticesApi(formValues, adminToken, dispatch);
      dispatch(setSubmissionLoaderReset());
      resetForm();
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setSubmissionLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function initiateFileImportsRequest(args = null) {
  return async (dispatch, getState) => {
    try {
      const { adminToken } = getState().adminAuth;
      dispatch(setFetchLoaderLoading());
      const imports = await fetchFileImportsApi(adminToken, args, dispatch);
      dispatch(fileImportsLoaded(imports));
      dispatch(setFetchLoaderReset());
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(fileImportsClear());
        dispatch(setFetchLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function clearFileImportsRequest() {
  return (dispatch) => {
    dispatch(fileImportsClear());
  };
}
